import React from 'react';
import { HomeIcon, ReceiptTextIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import { HowItWorks } from '../../components/home/HowItWorks';
import { SectionHeading, Card } from '../../components/ui/Panels';
import { ButtonLink } from '../../components/ui/Button';
const fulfillment = [{
  icon: <HomeIcon className="h-5 w-5" aria-hidden="true" data-id="element-2263" data-mp="3t4jh6v2lmszy" />,
  title: 'A chef cooks in your kitchen',
  body: 'They arrive with equipment, cook, serve, and clean down. Priced per guest with a set number of service hours included; extra hours are a visible add-on.',
  detail: 'Best for dinner parties, birthdays, anniversaries.'
}, {
  icon: <TruckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2264" data-mp="3e8k230h2nslb" />,
  title: 'A chef cooks at theirs and delivers',
  body: 'Cooked in the chef’s own certified kitchen and delivered chilled or hot, with reheating notes. Priced per portion with a minimum order.',
  detail: 'Best for weekly meal prep, small households, gatherings without kitchen space.'
}];
const pricing = [{
  title: 'Menu × guests',
  body: 'The base. Every menu names its price per guest, minimum head count, courses, and included hours.'
}, {
  title: 'Add-ons, itemized',
  body: 'Extra hours, an extra course, wine pairing, a server, deep cleanup, travel past the included radius. Each is a separate line.'
}, {
  title: 'Groceries, declared up front',
  body: 'Included in the price, billed at cost with receipts, or estimated per guest and trued up. You always know which before you accept.'
}, {
  title: 'Fees and tax, on the quote',
  body: 'A 10% service fee and local tax appear on the quote — not at a surprise checkout step.'
}];
export function HowItWorksPage() {
  return <>
      <section className="border-b border-hairline bg-oat" data-id="element-2265" data-mp="36urh067oi5oq">
        <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-2266" data-mp="3ulwwuldmcxz1">
          <p className="text-[13px] font-semibold uppercase tracking-[0.06em] text-action" data-id="element-2267" data-mp="3ujk7hn5j7lby">How it works</p>
          <h1 className="mt-4 max-w-[26ch] font-display text-[clamp(2.1rem,4vw,3.4rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-ink" data-id="element-2268" data-mp="38twwpdyq90q">
            You describe the occasion. A chef sends a real price.
          </h1>
          <p className="mt-5 max-w-[58ch] text-lg leading-relaxed text-ink-2" data-id="element-2269" data-mp="41rrjw5fhqhq">
            No hourly guesswork, no “contact for pricing”. Every chef publishes menus with a price per guest, and every quote
            breaks down what you are paying for.
          </p>
        </div>
      </section>

      <section aria-labelledby="fulfillment-heading" className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-2270" data-mp="3li9dwif01w0l">
        <SectionHeading level={2} title="Two ways a chef can feed you" data-id="element-2271" data-mp="3hgea979lj6jn" />
        <div className="mt-8 grid gap-5 lg:grid-cols-2" data-id="element-2272" data-mp="3cictiszn7jbc">
          {fulfillment.map(option => <Card key={option.title} className="p-6" data-id="element-2273" data-mp="37iqgajjte32j">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-soft text-primary" data-id="element-2274" data-mp="37i8cn1utomkl">
                {option.icon}
              </span>
              <h3 className="mt-4 font-display text-xl font-semibold text-ink" data-id="element-2275" data-mp="2seykukhpfmiq">{option.title}</h3>
              <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-ink-2" data-id="element-2276" data-mp="2xe1qa61ljur8">{option.body}</p>
              <p className="mt-3 text-[13px] text-ink-3" data-id="element-2277" data-mp="32f7lcaiqbu3x">{option.detail}</p>
            </Card>)}
        </div>
      </section>

      <section aria-labelledby="pricing-heading" className="border-y border-hairline bg-oat" data-id="element-2278" data-mp="37b6dm976hw9m">
        <div className="mx-auto max-w-shell px-5 py-16 lg:px-8 lg:py-20" data-id="element-2279" data-mp="29rbo94p9006m">
          <SectionHeading level={2} title="How the money is structured" hint="Chefs price menus, not hours. Everything else is a line you can see." data-id="element-2280" data-mp="2epd7bpmobzi3" />
          <ol className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-2281" data-mp="2of6iwqmtgemc">
            {pricing.map((item, i) => <li key={item.title} data-id="element-2282" data-mp="1qyhi909lriej" data-mpi={i}>
                <span className="tnum block font-display text-[13px] font-semibold text-action" data-id="element-2283" data-mp="1vy3vh9pfkync">0{i + 1}</span>
                <span className="mt-3 block h-px w-full bg-hairline" aria-hidden="true" data-id="element-2284" data-mp="1hlyh74y16nv" />
                <h3 className="mt-4 font-display text-lg font-semibold text-ink" data-id="element-2285" data-mp="3so6ja94enicg">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-2" data-id="element-2286" data-mp="b7or3685kilo">{item.body}</p>
              </li>)}
          </ol>
          <div className="mt-10 flex items-start gap-3 rounded-xl border border-hairline bg-surface p-5" data-id="element-2287" data-mp="6fxa9nestr8q">
            <ReceiptTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" data-id="element-2288" data-mp="k7bqjmhbnec3" />
            <p className="max-w-[70ch] text-sm leading-relaxed text-ink-2" data-id="element-2289" data-mp="g5qc3wp52rzs">
              A deposit (usually 20–40%) holds the date. The balance is charged after service. If groceries are billed at cost,
              receipts are attached to the booking before the balance goes through.
            </p>
          </div>
        </div>
      </section>

      <HowItWorks data-id="element-2290" data-mp="2vzqu4e57jazv" />

      <section aria-labelledby="safety-heading" className="mx-auto max-w-shell px-5 pb-20 lg:px-8 lg:pb-24" data-id="element-2291" data-mp="2qz2x9buarzxq">
        <Card className="grid gap-8 p-6 lg:grid-cols-[1fr_auto] lg:items-center lg:p-8" data-id="element-2292" data-mp="16gikllojtkll">
          <div data-id="element-2293" data-mp="1bgeozm15re1o">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary-soft text-primary" data-id="element-2294" data-mp="1bg51c53l5iz5">
              <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-2295" data-mp="wqftjr0zmgpw" />
            </span>
            <h2 id="safety-heading" className="mt-4 font-display text-2xl font-semibold tracking-[-0.02em] text-ink" data-id="element-2296" data-mp="11or3s2v72t8n">
              Allergies are handled as data, not as a note
            </h2>
            <p className="mt-3 max-w-[62ch] text-sm leading-relaxed text-ink-2" data-id="element-2297" data-mp="sqf7plbk738t">
              You record allergens with a severity — avoid, strict, or anaphylaxis risk — for each person in your household.
              They attach to every request, sit pinned above the chat, print on the quote, and the chef has to acknowledge them
              before a quote can be sent.
            </p>
          </div>
          <ButtonLink to="/onboarding" variant="primary" size="lg" data-id="element-2298" data-mp="2d3xongz3ujtm">
            Set up a dietary profile
          </ButtonLink>
        </Card>
      </section>
    </>;
}